// ============================================================
// Chess Arena — Chest Opening Modal
// Full-screen chest opening animation with reward reveal
// ============================================================

import type { Screen } from '../screenManager';
import type { ChestTier } from '../../core/types';
import gameConfig from '../../core/gameConfig';

const TIER_ICONS: Record<ChestTier, string> = {
  bronze: '/assets/ui/icon_chest_bronze.png',
  silver: '/assets/ui/icon_chest_silver.png',
  gold: '/assets/ui/icon_chest_gold.png',
  royal: '/assets/ui/icon_chest_royal.png',
};

const TIER_NAMES: Record<ChestTier, string> = {
  bronze: 'Bronze Chest',
  silver: 'Silver Chest',
  gold: 'Gold Chest',
  royal: 'Royal Chest',
};

interface ChestRewards {
  coins: number;
  shards?: Record<string, number>;
  charges?: Record<string, number>;
}

export class ChestOpeningModal implements Screen {
  readonly name = 'chestOpening';
  private container!: HTMLElement;
  private skipRequested = false;

  private onCollect: (() => void) | null = null;

  setOnCollect(cb: () => void): void {
    this.onCollect = cb;
  }

  init(container: HTMLElement): void {
    this.container = container;
    container.classList.add('chest-opening-screen');
  }

  show(data?: Record<string, unknown>): void {
    this.skipRequested = false;

    const tier = (data?.tier as ChestTier) ?? 'bronze';
    const rewards = (data?.rewards as ChestRewards) ?? { coins: 25, xp: 15 };

    // Build light rays
    const rayCount = 12;
    let raysHTML = '';
    for (let i = 0; i < rayCount; i++) {
      const angle = (360 / rayCount) * i;
      raysHTML += `<div class="chest-ray" style="transform: rotate(${angle}deg) translateY(-50%);"></div>`;
    }

    // Build reward items
    const rewardItems: { icon: string; label: string; color?: string }[] = [
      { icon: '\uD83D\uDCB0', label: `${rewards.coins} Coins` },
    ];

    const abilityCfg = gameConfig.isInitialized ? gameConfig.getAbilityConfig() : null;

    if (rewards.shards) {
      for (const [abilityId, count] of Object.entries(rewards.shards)) {
        const cfg = abilityCfg?.abilities?.[abilityId];
        const name = cfg?.displayName ?? abilityId;
        const color = cfg ? abilityCfg?.rarityColors?.[cfg.rarity] : undefined;
        rewardItems.push({ icon: '\uD83D\uDD39', label: `${count}x ${name} Shard${count > 1 ? 's' : ''}`, color });
      }
    }

    if (rewards.charges) {
      for (const [abilityId, count] of Object.entries(rewards.charges)) {
        const cfg = abilityCfg?.abilities?.[abilityId];
        const name = cfg?.displayName ?? abilityId;
        const color = cfg ? abilityCfg?.rarityColors?.[cfg.rarity] : undefined;
        rewardItems.push({ icon: '\u26A1', label: `${count}x ${name} Charge${count > 1 ? 's' : ''}`, color });
      }
    }

    this.container.innerHTML = `
      <div class="ca-backdrop" data-ref="backdrop">
        <div class="chest-opening-container">
          <!-- Light Rays -->
          <div class="chest-light-rays">${raysHTML}</div>

          <!-- Chest -->
          <div class="chest-opening-chest" data-ref="chest"><img src="${TIER_ICONS[tier]}" alt="${TIER_NAMES[tier]}" style="width: 100%; height: 100%;"></div>

          <!-- Tier Label -->
          <div style="font-family: var(--ca-font-title); font-size: 20px; color: var(--ca-gold); letter-spacing: 2px; opacity: 0; animation: rewards-appear 0.4s 0.8s ease forwards;">
            ${TIER_NAMES[tier]}
          </div>

          <!-- Rewards -->
          <div class="chest-rewards-list">
            ${rewardItems.map((r) => `
              <div class="chest-reward-item"${r.color ? ` style="border-left: 3px solid ${r.color}; padding-left: 8px;"` : ''}>
                <span class="chest-reward-icon">${r.icon}</span>
                <span>${r.label}</span>
              </div>
            `).join('')}
          </div>

          <!-- Collect Button -->
          <div class="chest-collect-btn">
            <button class="ca-btn ca-btn-gold ca-btn-lg" data-ref="collectBtn">Collect</button>
          </div>

          <!-- Tap hint -->
          <div class="chest-tap-hint">Tap to skip</div>
        </div>
      </div>
    `;

    // Tap to skip animations
    const backdrop = this.container.querySelector('[data-ref="backdrop"]')!;
    backdrop.addEventListener('click', (e) => {
      // If collect button was clicked, handle that separately
      if ((e.target as HTMLElement).closest('[data-ref="collectBtn"]')) return;

      if (!this.skipRequested) {
        this.skipAnimations();
      }
    });

    // Collect button
    const collectBtn = this.container.querySelector('[data-ref="collectBtn"]');
    collectBtn?.addEventListener('click', () => {
      this.onCollect?.();
    });
  }

  hide(): void {
    this.container.innerHTML = '';
    this.skipRequested = false;
  }

  update(): void {
    // No periodic updates
  }

  destroy(): void {
    this.container.innerHTML = '';
  }

  /** Skip all animations — show everything immediately. */
  private skipAnimations(): void {
    this.skipRequested = true;

    // Remove all animations and show everything
    const chest = this.container.querySelector('.chest-opening-chest') as HTMLElement;
    if (chest) {
      chest.style.animation = 'none';
    }

    const rays = this.container.querySelector('.chest-light-rays') as HTMLElement;
    if (rays) {
      rays.style.opacity = '1';
      rays.style.animation = 'none';
    }

    const rewardsList = this.container.querySelector('.chest-rewards-list') as HTMLElement;
    if (rewardsList) {
      rewardsList.style.opacity = '1';
      rewardsList.style.animation = 'none';
      rewardsList.querySelectorAll('.chest-reward-item').forEach((item) => {
        (item as HTMLElement).style.opacity = '1';
        (item as HTMLElement).style.animation = 'none';
      });
    }

    const collectDiv = this.container.querySelector('.chest-collect-btn') as HTMLElement;
    if (collectDiv) {
      collectDiv.style.opacity = '1';
      collectDiv.style.animation = 'none';
    }

    const hint = this.container.querySelector('.chest-tap-hint') as HTMLElement;
    if (hint) {
      hint.style.display = 'none';
    }
  }
}
